// src/components/ProjectPickerModal.jsx
import { useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FiEdit2 } from 'react-icons/fi';
import { useProjects } from '../context/ProjectContext';

/* Danish short date ------------------------------------------- */
const fmt = d =>
    new Date(d).toLocaleDateString('da-DK', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });

export default function ProjectPickerModal({ open, onClose, pendingHref }) {
    const {
        projects,
        saveProject,
        deleteProject,
        setActiveProjectName,
        activeProjectName,
    } = useProjects();

    /* ─── local state ──────────────────────────────────────────── */
    const [newName, setNewName] = useState('');
    const [err, setErr] = useState('');
    const [sortKey, setSortKey] = useState('updatedAt');
    const [sortDir, setSortDir] = useState('desc');

    /* rename-mode */
    const [editId, setEditId] = useState(null);
    const [draft, setDraft] = useState('');

    const listBoxRef = useRef(null);
    const inputRef = useRef(null);

    /* ─── derived: duplicate-name warning (no setState → no loop) */
    const dupNameMsg = useMemo(() => {
        const n = newName.trim();
        return !n
            ? ''
            : projects.some(p => p.name === n)
                ? 'Navnet findes allerede'
                : '';
    }, [newName, projects]);

    /* ─── helpers ──────────────────────────────────────────────── */
    const commitNew = () => {
        const n = newName.trim();

        if (!n) { setErr('Skriv et projektnavn'); return; }
        if (dupNameMsg) { setErr(dupNameMsg); return; }

        if (!projects.some(p => p.name === n)) saveProject(n);
        setActiveProjectName(n);
        onClose(pendingHref);

        /* reset local fields */
        setNewName('');
        setErr('');
    };

    const renameProject = (oldName, newVal) => {
        const n = newVal.trim();
        setEditId(null);

        if (!n || n === oldName) return;
        if (projects.some(p => p.name === n)) {
            alert('Navnet findes allerede');
            return;
        }

        /* copy old → new */
        const { name: _omit, ...rest } = projects.find(p => p.name === oldName) ?? {};
        saveProject(n, { ...rest, updatedAt: Date.now() });
        deleteProject(oldName);
        if (activeProjectName === oldName) setActiveProjectName(n);
    };

    const setSort = k => {
        if (sortKey === k) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        else {
            setSortKey(k);
            setSortDir(k === 'name' ? 'asc' : 'desc');
        }
    };
    const arrow = k => (k === sortKey ? (sortDir === 'asc' ? '▲' : '▼') : '');

    /* sorted list (memoised) */
    const rows = useMemo(() => {
        const list = [...projects];
        list.sort((a, b) =>
            sortKey === 'name'
                ? sortDir === 'asc'
                    ? a.name.localeCompare(b.name, 'da')
                    : b.name.localeCompare(a.name, 'da')
                : sortDir === 'asc'
                    ? a[sortKey] - b[sortKey]
                    : b[sortKey] - a[sortKey],
        );
        return list;
    }, [projects, sortKey, sortDir]);

    if (typeof window === 'undefined' || !open) return null;

    /* ─── modal layout ─────────────────────────────────────────── */
    return createPortal(
        <div
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,.35)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 5_000,
            }}
            onMouseDown={e =>
                listBoxRef.current && !listBoxRef.current.contains(e.target) && onClose()
            }
        >
            <div
                ref={listBoxRef}
                style={{
                    background: '#fff',
                    borderRadius: 8,
                    padding: '1.5rem',
                    width: 'min(520px,90%)',
                    maxHeight: '80vh',
                    overflowY: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '1rem',
                }}
                onMouseDown={e => e.stopPropagation()} /* keep clicks inside */
            >
                <h3 style={{ margin: 0 }}>Vælg eller opret projekt</h3>

                {/* — create new — */}
                <div style={{ display: 'flex', gap: '.6rem', alignItems: 'flex-end' }}>
                    <input
                        ref={inputRef}
                        className="bigSelect"
                        placeholder="Nyt projektnavn …"
                        style={{ flex: 1 }}
                        value={newName}
                        onChange={e => {
                            setNewName(e.target.value);
                            setErr('');
                        }}
                        onKeyDown={e => e.key === 'Enter' && commitNew()}
                    />
                    <button
                        style={okBtn}
                        onClick={commitNew}
                        disabled={!!(err || dupNameMsg)}
                    >
                        OK
                    </button>
                </div>

                {(err || dupNameMsg) && (
                    <small style={{ color: '#c00' }}>{err || dupNameMsg}</small>
                )}

                {/* — existing projects — */}
                <div style={{ position: 'relative' }}>
                    <table
                        style={{ width: '100%', borderCollapse: 'collapse' }}
                    >
                        <thead>
                            <tr>
                                <th onClick={() => setSort('name')}>Navn {arrow('name')}</th>
                                <th onClick={() => setSort('createdAt')} style={{ textAlign: 'right' }}>
                                    Oprettet {arrow('createdAt')}
                                </th>
                                <th onClick={() => setSort('updatedAt')} style={{ textAlign: 'right' }}>
                                    Ændret {arrow('updatedAt')}
                                </th>
                                <th style={{ width: 66 }} />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(p => (
                                <tr
                                    key={p.name}
                                    onClick={() => {
                                        if (editId) return;
                                        setActiveProjectName(p.name);
                                        onClose(pendingHref);
                                    }}
                                >
                                    <td>
                                        {editId === p.name ? (
                                            <input
                                                className="bigSelect"
                                                autoFocus
                                                value={draft}
                                                onChange={e => setDraft(e.target.value)}
                                                onBlur={() => renameProject(p.name, draft)}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') {
                                                        renameProject(p.name, draft);
                                                        e.currentTarget.blur();
                                                    }
                                                    e.stopPropagation();
                                                }}
                                            />
                                        ) : (
                                            p.name
                                        )}
                                    </td>
                                    <td style={{ textAlign: 'right' }}>{fmt(p.createdAt)}</td>
                                    <td style={{ textAlign: 'right' }}>{fmt(p.updatedAt)}</td>
                                    <td style={actionCellStyle} onClick={e => e.stopPropagation()}>
                                        <button
                                            className="iconBtn"
                                            title="Omdøb"
                                            onClick={() => {
                                                setEditId(p.name);
                                                setDraft(p.name);
                                            }}
                                            style={iconBtnStyle}
                                        >
                                            <FiEdit2 size={15} />
                                        </button>
                                        <button
                                            className="delBtn"
                                            title="Slet projekt"
                                            onClick={() =>
                                                confirm(`Slet projektet “${p.name}”?`) && deleteProject(p.name)
                                            }
                                        >
                                            ×
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <button
                    style={{ ...cancelBtn, alignSelf: 'flex-end' }}
                    onClick={() => onClose()}
                >
                    Luk
                </button>
            </div>

            {/* tiny style helpers */}
            <style jsx>{`
        th {
          padding: 0.35rem 0.5rem;
          background: #f7f7f7;
          cursor: pointer;
          user-select: none;
        }
        th:hover {
          background: #edeef9;
        }
        td {
          padding: 0.35rem 0.5rem;
        }
        tbody tr:hover td {
          background: #f5faff;
        }
      `}</style>
        </div>,
        document.body,
    );
}

/* ─── tiny button styles ─────────────────────────────────────── */
const okBtn = {
    background: '#0070f3',
    color: '#fff',
    border: 'none',
    padding: '.45rem 1.1rem',
    borderRadius: 4,
    fontWeight: 600,
    cursor: 'pointer',
};

const cancelBtn = {
    background: '#f5f5f5',
    color: '#333',
    border: '1px solid #ccc',
    padding: '.45rem 1.1rem',
    borderRadius: 4,
    cursor: 'pointer',
};

const actionCellStyle = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    whiteSpace: 'nowrap',
};

const iconBtnStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#555',
    lineHeight: 0,
};
